import React from 'react'
import { Box, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Scale, Landmark, FileDigit } from 'lucide-react'
import { useSearchTokens } from '../../SearchScreen/searchDesignTokens'

const CompanyLegalCard = ({ annonce }) => {
  const { t, i18n } = useTranslation()
  const dk = useSearchTokens()

  if (!annonce) return null

  const lang = i18n.language?.split('-')[0]
  const getLocalizedText = (textObj) => {
    if (!textObj) return null
    if (typeof textObj === 'string') return textObj
    return textObj[lang] || textObj.en || textObj.fr || textObj.ar || null
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return null
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: '2-digit', month: 'long', year: 'numeric',
      })
    } catch { return dateStr }
  }

  const identifiers = [
    annonce.ice && { label: 'ICE', value: annonce.ice },
    annonce.identifiant_fiscal && { label: t('id_fiscal', 'Identifiant fiscal'), value: annonce.identifiant_fiscal },
    annonce.taxe_professionnelle && { label: t('taxe_pro', 'Taxe professionnelle'), value: annonce.taxe_professionnelle },
    annonce.cnss && { label: 'CNSS', value: annonce.cnss },
  ].filter(Boolean)

  const depot = annonce.depot_legal
  const tribunalName = depot ? getLocalizedText(depot.tribunal) : null
  const dateDepot = depot ? formatDate(depot.date_depot) : null
  const hasDepot = tribunalName || dateDepot

  const hasContent = identifiers.length > 0 || hasDepot
  if (!hasContent) return null

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: '12px',
        border: `1px solid ${dk.border}`,
        backgroundColor: dk.surface,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Scale size={16} color={dk.gold} />
        <Typography
          sx={{
            fontSize: '0.82rem',
            fontWeight: 700,
            color: dk.text1,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          {t('identifiants_legaux', 'Identifiants légaux')}
        </Typography>
      </Box>

      {/* Identifier grid */}
      {identifiers.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 1.5,
            mb: hasDepot ? 2 : 0,
          }}
        >
          {identifiers.map(({ label, value }) => (
            <Box key={label} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Box sx={{ color: dk.text3, display: 'flex', mt: '3px', flexShrink: 0 }}>
                <FileDigit size={13} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: dk.text3, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {label}
                </Typography>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: dk.text1, fontFamily: '"JetBrains Mono", "Fira Code", monospace' }}>
                  {value}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Depot légal */}
      {hasDepot && (
        <Box
          sx={{
            pt: identifiers.length > 0 ? 2 : 0,
            borderTop: identifiers.length > 0 ? `1px solid ${dk.border}` : 'none',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
          }}
        >
          <Box sx={{ color: dk.text3, display: 'flex', mt: '3px', flexShrink: 0 }}>
            <Landmark size={13} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: dk.text3, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.3 }}>
              {t('depot_legal', 'Dépôt légal')}
            </Typography>
            {tribunalName && (
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: dk.text1 }}>
                {tribunalName}
              </Typography>
            )}
            {dateDepot && (
              <Typography sx={{ fontSize: '0.75rem', color: dk.text3, mt: 0.2 }}>
                {dateDepot}
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default CompanyLegalCard
